import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useColorTheme } from '../../context/ColorThemeContext';
import ConfirmationModal from '../ui/ConfirmationModal';
import OAuthProviderSettings from './OAuthProviderSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ThemeToggle = ({ adminToken }) => {
  const { colorTheme, setColorTheme, loading } = useColorTheme();
  const [showSaved, setShowSaved] = React.useState(false);
  
  // Auto-approve state
  const [autoApproveEnabled, setAutoApproveEnabled] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [loadingAutoApprove, setLoadingAutoApprove] = useState(true);
  const [savingAutoApprove, setSavingAutoApprove] = useState(false);
  const [autoApproveMessage, setAutoApproveMessage] = useState(null);
  
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    approveExisting: false
  });

  const handleThemeChange = async (newTheme) => {
    await setColorTheme(newTheme);
    // Show saved message
    setShowSaved(true);
    // Hide after 3 seconds
    setTimeout(() => {
      setShowSaved(false);
    }, 3000);
  };

  // Fetch auto-approve settings
  useEffect(() => {
    const fetchAutoApproveSettings = async () => {
      if (!adminToken) return;
      
      try {
        const response = await axios.get(`${API}/admin/system-settings/auto-approve`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        setAutoApproveEnabled(response.data.auto_approve_enabled || false);
        setWaitlistCount(response.data.waitlist_count || 0);
      } catch (err) {
        console.error('Error fetching auto-approve settings:', err);
      } finally {
        setLoadingAutoApprove(false);
      }
    };
    
    fetchAutoApproveSettings();
  }, [adminToken]);

  const handleAutoApproveToggle = () => {
    const newValue = !autoApproveEnabled;
    
    // If enabling and there are waitlist users, show confirmation modal
    if (newValue && waitlistCount > 0) {
      setConfirmModal({ isOpen: true, approveExisting: true });
    } else {
      // Just toggle without approving existing users
      executeAutoApproveChange(newValue, false);
    }
  };

  const executeAutoApproveChange = async (newValue, approveExisting) => {
    setSavingAutoApprove(true);
    setAutoApproveMessage(null);
    setConfirmModal({ isOpen: false, approveExisting: false });
    
    try {
      const response = await axios.put(
        `${API}/admin/system-settings/auto-approve`,
        {
          auto_approve_enabled: newValue,
          approve_existing_waitlist: approveExisting
        },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      
      setAutoApproveEnabled(newValue);
      
      if (response.data.approved_count > 0) {
        setAutoApproveMessage({
          type: 'success',
          text: `✅ Auto-approve enabled! ${response.data.approved_count} waitlist users have been approved.`
        });
        setWaitlistCount(0);
      } else {
        setAutoApproveMessage({
          type: 'success',
          text: newValue 
            ? '✅ Auto-approve enabled! New users will be approved automatically.'
            : '✅ Auto-approve disabled. New users will go to waitlist.'
        });
      }
      
      setTimeout(() => setAutoApproveMessage(null), 5000);
    } catch (err) {
      setAutoApproveMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to update setting'
      });
    } finally {
      setSavingAutoApprove(false);
    }
  };

  const handleConfirmApproveAll = () => {
    executeAutoApproveChange(true, true);
  };

  const handleSkipApproveExisting = () => {
    executeAutoApproveChange(true, false);
    setConfirmModal({ isOpen: false, approveExisting: false });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Confirmation Modal for approving waitlist users */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, approveExisting: false })}
        onConfirm={handleConfirmApproveAll}
        title="Approve Waitlist Users?"
        message={
          <div>
            <p className="mb-3">You are enabling auto-approve for new signups.</p>
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 rounded-lg p-3 mb-3">
              <p className="text-orange-700 dark:text-orange-300 font-medium">
                ⏳ {waitlistCount} user{waitlistCount !== 1 ? 's are' : ' is'} currently waiting for approval
              </p>
            </div>
            <p>Do you want to <strong>approve all waitlist users</strong> now?</p>
          </div>
        }
        type="success"
        icon="✅"
        confirmText={`Approve All (${waitlistCount})`}
        cancelText="Skip for Now"
        isProcessing={savingAutoApprove}
      />

      {/* Auto-Approve Settings Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            🔐 Signup Settings
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Control how new users are onboarded to the platform
          </p>
        </div>

        {/* Auto-approve Message */}
        {autoApproveMessage && (
          <div className={`mb-4 p-4 rounded-lg ${
            autoApproveMessage.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-500 text-green-700 dark:text-green-300' 
              : 'bg-red-50 dark:bg-red-900/20 border border-red-500 text-red-700 dark:text-red-300'
          }`}>
            {autoApproveMessage.text}
          </div>
        )}

        {/* Auto-approve Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xl">👤</span>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Auto-approve new signups
              </p>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-7">
              {autoApproveEnabled 
                ? 'Users can login immediately after signup' 
                : 'Users need admin approval before they can login'}
            </p>
            {waitlistCount > 0 && !autoApproveEnabled && (
              <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 ml-7">
                ⏳ {waitlistCount} user{waitlistCount !== 1 ? 's' : ''} waiting for approval
              </p>
            )}
          </div>
          
          <button
            onClick={handleAutoApproveToggle}
            disabled={loadingAutoApprove || savingAutoApprove}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
              autoApproveEnabled 
                ? 'bg-green-500 focus:ring-green-500' 
                : 'bg-gray-300 dark:bg-gray-600 focus:ring-gray-500'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                autoApproveEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Info Box */}
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start">
            <span className="text-xl mr-3">💡</span>
            <div>
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                When to use auto-approve?
              </h3>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                Enable auto-approve when you have Stripe integration set up, so paying customers can access the platform immediately. 
                Disable it if you want to manually review each signup.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Theme Settings Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            🎨 Theme Settings
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Choose your preferred color theme for the application
          </p>
        </div>

        <div className="space-y-4">
          {/* Success Message */}
          {showSaved && (
            <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg p-4 flex items-center animate-pulse">
              <div className="flex-shrink-0">
                <span className="text-2xl">✅</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  Theme preference saved successfully!
                </p>
                <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                  Your theme will be applied across all pages
                </p>
              </div>
            </div>
          )}

          {/* Current Theme Display */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Current Theme</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white capitalize">
                {colorTheme} Theme {colorTheme === 'green' ? '🟢' : colorTheme === 'purple' ? '🟣' : '🔵'}
              </p>
            </div>
          </div>

          {/* Theme Selection Grid */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            {/* Green Theme Card */}
            <button
              onClick={() => handleThemeChange('green')}
              className={`p-4 rounded-lg border-2 transition-all transform hover:scale-105 ${
                colorTheme === 'green' 
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-lg' 
                  : 'border-gray-200 bg-gray-50 dark:bg-gray-700 hover:border-green-300'
              }`}
            >
              <div className="flex items-center justify-center mb-2">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-600 to-emerald-600"></div>
              </div>
              <p className="text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                Green
              </p>
              {colorTheme === 'green' && (
                <p className="text-center text-xs text-green-600 dark:text-green-400 mt-1 font-semibold">
                  ✓ Active
                </p>
              )}
            </button>

            {/* Purple Theme Card */}
            <button
              onClick={() => handleThemeChange('purple')}
              className={`p-4 rounded-lg border-2 transition-all transform hover:scale-105 ${
                colorTheme === 'purple' 
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-lg' 
                  : 'border-gray-200 bg-gray-50 dark:bg-gray-700 hover:border-purple-300'
              }`}
            >
              <div className="flex items-center justify-center mb-2">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600"></div>
              </div>
              <p className="text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                Purple
              </p>
              {colorTheme === 'purple' && (
                <p className="text-center text-xs text-purple-600 dark:text-purple-400 mt-1 font-semibold">
                  ✓ Active
                </p>
              )}
            </button>

            {/* Blue Theme Card */}
            <button
              onClick={() => handleThemeChange('blue')}
              className={`p-4 rounded-lg border-2 transition-all transform hover:scale-105 ${
                colorTheme === 'blue' 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-lg' 
                  : 'border-gray-200 bg-gray-50 dark:bg-gray-700 hover:border-blue-300'
              }`}
            >
              <div className="flex items-center justify-center mb-2">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-600 to-sky-500"></div>
              </div>
              <p className="text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                Blue
              </p>
              {colorTheme === 'blue' && (
                <p className="text-center text-xs text-blue-600 dark:text-blue-400 mt-1 font-semibold">
                  ✓ Active
                </p>
              )}
            </button>
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <span className="text-2xl">ℹ️</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  About Theme Settings
                </h3>
                <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                  The theme change will apply across the entire application including the landing page, dashboard, and all screens. Both light and dark modes are supported for each theme.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* OAuth Provider Settings */}
      <OAuthProviderSettings adminToken={adminToken} />
    </div>
  );
};

export default ThemeToggle;
